export const MAX_NOTE_ATTACHMENTS = 20;
export const MAX_NOTE_ATTACHMENT_BYTES = 100 * 1024 * 1024;

const SUPPORTED_EXTENSION = /\.(?:jpe?g|png|webp|gif|pdf|docx?|xlsx?|csv)$/i;

export const NOTE_ATTACHMENT_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv";

export const validateNoteAttachmentFiles = (
  current: readonly unknown[],
  selected: readonly File[]
): {accepted: File[]; errors: string[]} => {
  const accepted: File[] = [];
  const errors: string[] = [];
  let remaining = Math.max(0, MAX_NOTE_ATTACHMENTS - current.length);

  for (const file of selected) {
    if (remaining === 0) {
      if (!errors.includes("You can attach up to 20 files to one note.")) {
        errors.push("You can attach up to 20 files to one note.");
      }
      continue;
    }
    if (!SUPPORTED_EXTENSION.test(file.name)) {
      errors.push(`${file.name} is not a supported attachment type.`);
      continue;
    }
    if (file.size > MAX_NOTE_ATTACHMENT_BYTES) {
      errors.push(`${file.name} is larger than 100 MB.`);
      continue;
    }
    accepted.push(file);
    remaining -= 1;
  }

  return {accepted, errors};
};

interface QueuedUpload<T> {
  epoch: number;
  task: () => Promise<T>;
  cleanup?: (value: T) => void | Promise<void>;
  resolve: (value: T | undefined) => void;
  reject: (reason: unknown) => void;
}

export interface NoteAttachmentUploadQueue {
  run<T>(task: () => Promise<T>, cleanup?: (value: T) => void | Promise<void>): Promise<T | undefined>;
  discard(): void;
}

/**
 * Small per-composer scheduler. Discarding advances its epoch: queued work is dropped, while an upload
 * already accepted by the server is deleted through `cleanup` when that late response arrives.
 */
export const createNoteAttachmentUploadQueue = (
  concurrency: number
): NoteAttachmentUploadQueue => {
  const limit = Math.max(1, Math.floor(concurrency));
  let active = 0;
  let epoch = 0;
  const waiting: QueuedUpload<unknown>[] = [];

  const drain = (): void => {
    while (active < limit && waiting.length) {
      const item = waiting.shift();
      if (!item) return;
      if (item.epoch !== epoch) {
        item.resolve(undefined);
        continue;
      }
      active += 1;
      item.task().then(
        async (value) => {
          if (item.epoch !== epoch) {
            try {
              await item.cleanup?.(value);
            } catch {
              // Draft deletion is best effort. Expiry remains the fallback, and cleanup failure must not
              // strand the discarded caller or block the next upload in this composer.
            }
            item.resolve(undefined);
          } else {
            item.resolve(value);
          }
        },
        item.reject
      ).finally(() => {
        active -= 1;
        drain();
      });
    }
  };

  return {
    run<T>(task: () => Promise<T>, cleanup?: (value: T) => void | Promise<void>): Promise<T | undefined> {
      return new Promise<T | undefined>((resolve, reject) => {
        waiting.push({
          epoch,
          task,
          cleanup: cleanup as ((value: unknown) => void | Promise<void>) | undefined,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
        drain();
      });
    },
    discard(): void {
      epoch += 1;
      waiting.splice(0).forEach((item) => item.resolve(undefined));
    },
  };
};
